import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import AuthForm from "./components/AuthForm";
import GeneralInfoForm from "./components/GeneralInfoForm";
import InterestOfferingForm from "./components/InterestOfferingForm";
import AvailabilityForm from "./components/AvailabilityForm";
import FindPartner from "./components/FindPartner";
import MatchesDashboard from './MatchesDashboard';
import HomePage from "./components/HomePage";
import { ThemeProvider, CssBaseline, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, AppBar, Typography, Box, Button, IconButton, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import theme from './theme';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HomeIcon from '@mui/icons-material/Home';
import logo from "./assets/skill-exchange-logo.png";

function App() {
  const [profileVersion, setProfileVersion] = useState(0);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) {
        setProfile(null);
        setProfileLoading(false);
        return;
      }
      setProfileLoading(true);
      const userRef = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(userRef);
      setProfile(snap.exists() ? snap.data() : {});
      setProfileLoading(false);
      setActiveTab('general'); // Go to General Info after login
    });
    return unsub;
  }, [profileVersion]);

  const handleProfileSaved = () => {
    setProfileVersion(v => v + 1);
  };

  const navItems = [
    { key: 'home', label: 'Home', icon: <HomeIcon /> },
    { key: 'general', label: 'General Information', icon: <AccountCircleIcon /> },
    { key: 'interest', label: 'My Interest & Offering', icon: <LocalOfferIcon /> },
    { key: 'availability', label: 'My Availability', icon: <AccessTimeIcon /> },
    { key: 'dashboard', label: 'Skill Exchange Dashboard', icon: <DashboardIcon /> },
    { key: 'find', label: 'Find My Learning Partner', icon: <PeopleAltIcon /> }
  ];

  const drawerContent = (
    <>
      <Toolbar />
      <List>
        {navItems.map(item => (
          <ListItemButton
            key={item.key}
            selected={activeTab === item.key}
            onClick={() => {
              setActiveTab(item.key);
              setMobileOpen(false); // close drawer on mobile
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <img src={logo} alt="Skill Exchange Logo" style={{ height: 38, marginRight: 12, borderRadius: 10 }} aria-label="Skill Exchange App Logo" />
            </Box>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Skill Exchange App
            </Typography>
            {user ? (
              <>
                <Button color="inherit" onClick={() => signOut(auth)}>Sign Out</Button>
                <Typography variant="body2" sx={{ ml: 2 }}>{user.email}</Typography>
              </>
            ) : (
              <>
                <Button color="inherit" onClick={() => setActiveTab('signin')}>Sign In</Button>
                <Button color="inherit" onClick={() => setActiveTab('signup')}>Sign Up</Button>
              </>
            )}
          </Toolbar>
        </AppBar>
        {/* Responsive Drawer */}
        <Box component="nav" sx={{ width: { sm: 240 }, flexShrink: { sm: 0 } }} aria-label="navigation drawer">
          {/* Temporary drawer for mobile */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240, pt: 8 },
            }}
          >
            {drawerContent}
          </Drawer>
          {/* Permanent drawer for desktop */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240, pt: 8 },
            }}
            open
          >
            {drawerContent}
          </Drawer>
        </Box>
        {/* Main content uses full width on mobile */}
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, minHeight: '100vh', display: 'flex', flexDirection: 'column', width: { xs: '100vw', sm: 'auto' } }}>
          <Toolbar />
          <Box sx={{ flexGrow: 1 }}>
            {activeTab === "home" && <HomePage />}
            {!user && activeTab !== "home" && (
              <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Please sign up or log in to create your profile and exchange skills with others!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Unlock the full power of Skill Exchange: connect with learning partners, showcase your skills, and schedule sessions.
                </Typography>
                <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={() => setActiveTab('signup')}>
                  Sign Up
                </Button>
                <Button variant="outlined" color="primary" onClick={() => setActiveTab('signin')}>
                  Sign In
                </Button>
              </Box>
            )}
            {!user && activeTab === "signin" && <AuthForm mode="signin" onSuccess={() => setActiveTab('general')} />}
            {!user && activeTab === "signup" && <AuthForm mode="signup" onSuccess={() => setActiveTab('general')} />}
            {user && profileLoading && <div className="loading-overlay">Loading...</div>}
            {user && !profileLoading && (
              <>
                {activeTab === "general" && <GeneralInfoForm profile={profile} onSaved={handleProfileSaved} />}
                {activeTab === "interest" && <InterestOfferingForm profile={profile} onSaved={handleProfileSaved} />}
                {activeTab === "availability" && <AvailabilityForm profile={profile} onSaved={handleProfileSaved} />}
                {activeTab === "dashboard" && <MatchesDashboard />}
                {activeTab === "find" && <FindPartner />}
              </>
            )}
          </Box>
          <Box component="footer" sx={{ textAlign: 'center', py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Connect: &nbsp;
              <a href="https://linkedin.com/in/harithapliyal" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#0A66C2', fontWeight: 500 }}>LinkedIn</a>
              &nbsp;|&nbsp;
              <a href="https://twitter.com/dasarpai" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1DA1F2', fontWeight: 500 }}>Twitter</a>
              &nbsp;|&nbsp;
              <a href="https://dasarpai.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}>Website</a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function ensureArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(Boolean);
  return [];
}

function ensureAvailability(value) {
  if (!value || typeof value !== 'object') return {};
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const result = {};
  days.forEach(day => {
    const hours = value[day];
    result[day] = Array.isArray(hours) ? hours.map(Number) : [];
  });
  return result;
}

export default App;